/** @jsxImportSource @emotion/react */
import { Spinner } from "../common/Spinner";
import { OrganizationKeyName } from "../../model/Organization";
import {
  useAutoAddedProducts,
  useDeleteAutoAddedProduct,
} from "../../service/api";
import { StyledText } from "../../assets/typography/typography";
import { colors } from "../../components/colors";
import { formatMoneyAmount } from "../../util";
import { PrimaryButton } from "../../components/Buttons";
import { useHandleFetchError } from "../../error-handler";
import { css } from "@emotion/react";
import { CardContainer } from "../../components/page-skeleton/CardContainer";
import { PageHeader } from "../../components/page-skeleton/PageHeader";
import { AddAutoAddedProductSelector } from "./AddAutoAddedProductSelector";

interface Props {
  organizationKeyName: OrganizationKeyName;
  shopId?: string;
}

// Hacking width until all cards on page are updated
const cardAdditionalStyling = css`
  max-width: 1174px !important;
`;

// Normally we would use PageWrapper component, but other components on the page are not updated yet
const cardWrapperStyle = css`
  padding-left: 24px;
  padding-right: 24px;
  display: flex;
  width: 100%;
  justify-content: center;
`;

export const AutoAddedProducts = ({ organizationKeyName, shopId }: Props) => {
  const {
    data: autoAddedProducts,
    isLoading: autoAddedProductsLoading,
    error: autoAddedProductsError,
  } = useAutoAddedProducts(organizationKeyName, shopId);

  const {
    mutateAsync: deleteAutoAddedProduct,
    isLoading: deleteAutoAddedProductLoading,
  } = useDeleteAutoAddedProduct();

  useHandleFetchError(autoAddedProductsError);

  if (autoAddedProductsLoading) {
    return <Spinner />;
  }

  if (!autoAddedProducts) {
    return null;
  }

  if (!shopId) {
    return null;
  }

  return (
    <div css={cardWrapperStyle}>
      <CardContainer additionalStyling={cardAdditionalStyling}>
        <PageHeader title="Auto added products">
          <AddAutoAddedProductSelector />
        </PageHeader>
        <div className="table-responsive">
          <table className="table table-hover table-outline table-vcenter card-table">
            <thead>
              <tr className="border-top">
                <th>
                  <StyledText
                    className="bodySubtextRegular mb-0"
                    color={colors.textSecondary}
                  >
                    Name
                  </StyledText>
                </th>
                <th>
                  <StyledText
                    className="bodySubtextRegular mb-0"
                    color={colors.textSecondary}
                  >
                    Trigger product
                  </StyledText>
                </th>
                <th>
                  <StyledText
                    className="bodySubtextRegular mb-0"
                    color={colors.textSecondary}
                  >
                    Price
                  </StyledText>
                </th>
                <th className="w-1"></th>
              </tr>
            </thead>
            <tbody>
              {autoAddedProducts.map((autoAddedProduct) => {
                return (
                  <tr key={autoAddedProduct.id}>
                    <td>
                      <StyledText
                        className="bodyTextRegular mb-0"
                        color={colors.textPrimary}
                      >
                        {autoAddedProduct.product.name}
                      </StyledText>
                    </td>
                    <td>
                      <StyledText
                        className="bodyTextRegular mb-0"
                        color={colors.textPrimary}
                      >
                        {autoAddedProduct.triggerProduct
                          ? autoAddedProduct.triggerProduct.name
                          : "-"}
                      </StyledText>
                    </td>
                    <td>
                      <StyledText
                        className="bodyTextRegular mb-0"
                        color={colors.textPrimary}
                      >
                        {formatMoneyAmount(
                          autoAddedProduct.product.price.amount || 0,
                          autoAddedProduct.product.price.currency
                        )}
                      </StyledText>
                    </td>
                    <td className="text-right">
                      <div className="btn-group">
                        <PrimaryButton
                          color={colors.errorDark}
                          backgroundColor="transparent"
                          disabled={deleteAutoAddedProductLoading}
                          hoverColor={colors.errorLight}
                          onClick={() => {
                            deleteAutoAddedProduct({
                              organizationKeyName,
                              data: {
                                shopId,
                                id: autoAddedProduct.id,
                              },
                            });
                          }}
                          className="mr-2"
                        >
                          Delete
                        </PrimaryButton>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContainer>
    </div>
  );
};
